// src/hooks/useBoardMutations.ts
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryKeys';
import { useInvalidation } from '../lib/invalidation';
import { saveContentToFirestore } from '../features/collaboration/contentSaveUtils';
import { saveTitleOnlyWithAllData, updateTitleRelatedDataOnly } from '../features/collaboration/titleSaveUtils';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { assertWorkspaceInvariant } from '../utils/workspaceInvariant';

interface ContentSaveOptions {
  workspaceId: string;
  boardId: string;
  userId: string;
  ydoc: any;
  isHost: boolean;
  saveReason: 'edit-triggered' | 'watchdog-save' | 'final-exit';
  currentTitle?: string;
  editorContentJSON?: any;
}

interface TitleSaveOptions {
  newTitle: string;
  workspaceId: string;
  boardId: string;
  userId: string;
  ydoc?: any;
  editorContentJSON?: any;
}

interface PinToggleOptions {
  workspaceId: string;
  boardId: string;
  pinned: boolean;
}

export const useBoardMutations = () => {
  const queryClient = useQueryClient();
  const { invalidateWorkspace } = useInvalidation();

  // コンテンツ保存
  const saveContentMutation = useMutation({
    mutationFn: async (options: ContentSaveOptions) => {
      const result = await saveContentToFirestore(options);
      return result;
    },
    onSuccess: (_, variables) => {
      // ボード一覧を無効化
      invalidateWorkspace(variables.workspaceId);
    },
  });

  // タイトル保存（全データ）
  const saveTitleMutation = useMutation({
    mutationFn: async (options: TitleSaveOptions) => {
      // generateUniqueTitle関数の実装が必要
      const generateUniqueTitle = async (title: string) => title;
      const result = await saveTitleOnlyWithAllData(
        options.newTitle,
        options.workspaceId,
        options.boardId,
        options.userId,
        options.ydoc,
        generateUniqueTitle,
        '', // oldTitle
        undefined // updateLinksCallback
      );
      return result;
    },
    onSuccess: (_, variables) => {
      // ボード一覧を無効化
      invalidateWorkspace(variables.workspaceId);
    },
  });

  // タイトル関連データのみ更新
  const updateTitleDataMutation = useMutation({
    mutationFn: async (options: Omit<TitleSaveOptions, 'ydoc' | 'editorContentJSON'>) => {
      const result = await updateTitleRelatedDataOnly(
        options.newTitle,
        options.workspaceId,
        options.boardId,
        options.userId
      );
      return result;
    },
    onSuccess: (_, variables) => {
      // ボード一覧を無効化
      invalidateWorkspace(variables.workspaceId);
    },
  });

  // ピン状態の切り替え
  const togglePinMutation = useMutation({
    mutationFn: async (options: PinToggleOptions) => {
      assertWorkspaceInvariant(options.workspaceId, { source: 'boards.togglePin' });
      const boardRef = doc(db, `workspaces/${options.workspaceId}/boards/${options.boardId}`);
      await updateDoc(boardRef, { 
        pinned: options.pinned,
        lastModified: serverTimestamp()
      });
      return true;
    },
    onSuccess: (_, variables) => {
      // ボード一覧を無効化
      invalidateWorkspace(variables.workspaceId);
    },
  });

  return {
    saveContent: saveContentMutation.mutateAsync,
    saveTitle: saveTitleMutation.mutateAsync,
    updateTitleData: updateTitleDataMutation.mutateAsync,
    togglePin: togglePinMutation.mutateAsync,
    isSavingContent: saveContentMutation.isPending,
    isSavingTitle: saveTitleMutation.isPending,
    isUpdatingTitle: updateTitleDataMutation.isPending,
    isTogglingPin: togglePinMutation.isPending,
  };
};
